import React from "react";
import PropTypes from "prop-types";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
//import SFTypography from "../typography";
import { useLocation, Link } from "react-router-dom";
import { TabPanel } from "@mui/lab";
import getStyled from "../../hooks/getStyled";

const { styled } = getStyled();

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "autoWidthTabs",
})(({ theme, autoWidthTabs }) => ({
  ...(autoWidthTabs
    ? {
      paddingLeft: 0,
      paddingRight: 0,
      marginRight: theme.spacing(3),
      minWidth: "auto",
    }
    : {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    }),
}));

export const SFTab = ({ disabled = false, label }) => (
  <Tab disabled={disabled} label={label} />
);

SFTab.propTypes = {
  /* Determines whether the tab is clickable */
  disabled: PropTypes.bool,
  /* The text displayed for the tab */
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
};

export const SFTabs = ({
  orientation = "horizontal",
  children,
  onChange,
  value,
  autoWidthTabs,
  className,
}) => (
  <Tabs
    orientation={orientation}
    value={value}
    onChange={onChange}
    className={className}
  >
    {children.map(
      (child, index) =>
        !child.props.hidden && (
          <StyledTab
            key={index}
            value={index}
            disabled={child.props.disabled}
            label={child.props.label}
            autoWidthTabs={autoWidthTabs}
          />
        )
    )}
  </Tabs>
);

SFTabs.propTypes = {
  /* Default event for chaning the state value of the active tab */
  onChange: PropTypes.func.isRequired,
  /* Tab orientation */
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  /* The actual SFTabs */
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  /* The value to decide which tab is active. Should be based on the tab index */
  value: PropTypes.number.isRequired,
  /* This property indicates that the active indicator of the tab (border underneath) will fit with the tab */
  autoWidthTabs: PropTypes.bool,
  /* The class name for the tabs */
  className: PropTypes.string,
};

export default SFTabs;

export const SFNavTabs = ({
  orientation = "horizontal",
  scrollableTabs = false,
  autoWidthTabs = false,
  children,
}) => {
  const location = useLocation();
  const activeTab = children.find((child) =>
    location.pathname.includes(child.props.path)
  );

  return (
    <Tabs
      value={activeTab ? activeTab.props.path : location.pathname}
      orientation={orientation}
      {...(scrollableTabs && {
        variant: "scrollable",
        scrollButtons: "auto",
        allowScrollButtonsMobile: true,
      })}
    >
      {children.map((child, index) => (
        <StyledTab
          key={index}
          value={child.props.path}
          disabled={child.props.disabled}
          component={Link}
          to={child.props.path}
          label={child.props.label}
          autoWidthTabs={autoWidthTabs}
        />
      ))}
    </Tabs>
  );
};

SFNavTabs.propTypes = {
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  /*
   ** This property indicates that the tabs will be scrollable if they don't fit in the page
   ** Example: the tabs in the benchmark screen
   */
  scrollableTabs: PropTypes.bool,
  /* This property indicates that the active indicator of the tab (border underneath) will fit with the tab */
  autoWidthTabs: PropTypes.bool,
};

export const SFNavTab = ({ disabled = false, label, path }) => {
  return (
    <Tab
      value={path}
      disabled={disabled}
      component={Link}
      to={path}
      label={label}
    />
  );
};

SFNavTab.propTypes = {
  path: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
  disabled: PropTypes.bool,
};

export const SFTabPanel = ({ value, child }) => {
  return <TabPanel value={value}>{child}</TabPanel>;
};

SFTabPanel.propTypes = {
  value: PropTypes.string,
  child: PropTypes.node,
};

// export const SFTabContext = ({value}) =>{
//   return (
//     <TabContext></TabContext>
//   );
// }
